// Costanti condivise tra backend e frontend.
// Single source of truth per evitare magic numbers/strings sparsi.
/** Ruoli admin con privilegi differenziati. */
export const ADMIN_ROLES = ['master', 'operator', 'viewer'];
/** Ambienti runtime. Letti da `INSTANCE_ENV`. */
export const INSTANCE_ENVS = ['development', 'staging', 'production'];
/** Auth: argon2id parametri (raccomandati OWASP 2024). */
export const ARGON2_PARAMS = {
    memoryCost: 64 * 1024, // 64 MB
    timeCost: 3,
    parallelism: 4,
};
/** Lockout account dopo N login falliti. */
export const LOGIN_FAILED_THRESHOLD = 5;
export const LOGIN_LOCKOUT_MINUTES = 15;
/** Password policy (admin). */
export const PASSWORD_MIN_LENGTH = 12;
/** JWT durate. */
export const JWT_ACCESS_TTL = '15m';
export const JWT_REFRESH_TTL = '7d';
/** Cookie name (prefix __Host- per sicurezza extra). */
export const COOKIE_ACCESS_TOKEN = '__Host-audiq_at';
export const COOKIE_REFRESH_TOKEN = '__Host-audiq_rt';
/** Audit actions standard (esteso negli sprint successivi). */
export const AUDIT_ACTIONS = {
    ADMIN_LOGIN_SUCCESS: 'admin_login_success',
    ADMIN_LOGIN_FAILED: 'admin_login_failed',
    ADMIN_LOGIN_LOCKED: 'admin_login_locked',
    ADMIN_LOGOUT: 'admin_logout',
    ADMIN_TOKEN_REFRESH: 'admin_token_refresh',
    SEED_EXECUTED: 'seed_executed',
    SEED_ADMIN_CREATED: 'seed_admin_created',
    // Flow management
    FLOW_CREATE: 'flow_create',
    FLOW_UPDATE: 'flow_update',
    FLOW_DELETE: 'flow_delete',
    FLOW_PUBLISH: 'flow_publish',
    FLOW_DUPLICATE: 'flow_duplicate',
    FLOW_QUESTION_CREATE: 'flow_question_create',
    FLOW_QUESTION_UPDATE: 'flow_question_update',
    FLOW_QUESTION_DELETE: 'flow_question_delete',
    FLOW_QUESTION_REORDER: 'flow_question_reorder',
    FLOW_OUTCOME_CREATE: 'flow_outcome_create',
    FLOW_OUTCOME_UPDATE: 'flow_outcome_update',
    FLOW_OUTCOME_DELETE: 'flow_outcome_delete',
    // Sessione (Sprint 2.7+)
    SESSION_CONSENT_GIVEN: 'session_consent_given',
    SESSION_STARTED: 'session_started',
    PICKUP_CODE_GENERATED: 'pickup_code_generated',
    // Sprint 3 — identità & conversation
    IDENTITY_COLLECTED: 'identity_collected',
    IDENTITY_DECRYPT: 'identity_decrypt',
    SESSION_TURN_RECEIVED: 'session_turn_received',
    SESSION_TURN_LLM_OK: 'session_turn_llm_ok',
    SESSION_TURN_LLM_ERROR: 'session_turn_llm_error',
    SESSION_EMERGENCY_TRIGGERED: 'session_emergency_triggered',
    SESSION_COMPLETED: 'session_completed',
    SESSION_ABANDONED: 'session_abandoned',
    PATIENT_VIEW: 'patient_view',
    ENCOUNTER_VIEW: 'encounter_view',
    // Sprint 4
    MESSAGE_TRANSLATED_IT: 'message_translated_it',
    ENCOUNTER_AUTO_ABANDONED: 'encounter_auto_abandoned',
    ENCOUNTER_REVIEWED: 'encounter_reviewed',
    ENCOUNTER_MARKED_CLINICAL: 'encounter_marked_clinical',
    PATIENT_NOTES_UPDATED: 'patient_notes_updated',
    PATIENT_SEARCH_BY_NAME: 'patient_search_by_name',
    PDF_GENERATED: 'pdf_generated',
    PDF_DOWNLOADED: 'pdf_downloaded',
    LIMITS_UPDATED: 'limits_updated',
    SESSION_BLOCKED_HARD_LIMIT: 'session_blocked_hard_limit',
    SOFT_LIMIT_ALERT_SENT: 'soft_limit_alert_sent',
    CRON_USAGE_AGGREGATED: 'cron_usage_aggregated',
    CRON_IDLE_TIMEOUT_RUN: 'cron_idle_timeout_run',
    CRON_TRIGGERED_MANUAL: 'cron_triggered_manual',
    // Sprint 5 — settings
    SETTINGS_BRANDING_UPDATED: 'settings_branding_updated',
    SETTINGS_LLM_UPDATED: 'settings_llm_updated',
    SETTINGS_LLM_CLIENT_KEY_SET: 'settings_llm_client_key_set',
    SETTINGS_LLM_CLIENT_KEY_REMOVED: 'settings_llm_client_key_removed',
    SETTINGS_LIMITS_UPDATED: 'settings_limits_updated',
    SETTINGS_RETENTION_UPDATED: 'settings_retention_updated',
    SETTINGS_SMTP_UPDATED: 'settings_smtp_updated',
    SETTINGS_SMTP_TEST_SENT: 'settings_smtp_test_sent',
    SETTINGS_PRIVACY_UPDATED: 'settings_privacy_updated',
    SETTINGS_IDENTITY_UPDATED: 'settings_identity_updated',
    BRANDING_LOGO_UPLOADED: 'branding_logo_uploaded',
    // Sprint 5 — admin user management
    ADMIN_USER_CREATED: 'admin_user_created',
    ADMIN_USER_UPDATED: 'admin_user_updated',
    ADMIN_USER_DELETED: 'admin_user_deleted',
    ADMIN_USER_PASSWORD_RESET: 'admin_user_password_reset',
    ADMIN_USER_LOCKED: 'admin_user_locked',
    ADMIN_USER_UNLOCKED: 'admin_user_unlocked',
    ADMIN_USER_2FA_RESET: 'admin_user_2fa_reset',
    ADMIN_PASSWORD_CHANGED: 'admin_password_changed',
    // Sprint 5 — 2FA TOTP
    TOTP_SETUP_INIT: 'totp_setup_init',
    TOTP_ENABLED: 'totp_enabled',
    TOTP_DISABLED: 'totp_disabled',
    TOTP_VERIFIED: 'totp_verified',
    TOTP_FAILED: 'totp_failed',
    TOTP_RECOVERY_CODE_USED: 'totp_recovery_code_used',
    TOTP_RECOVERY_CODES_REGENERATED: 'totp_recovery_codes_regenerated',
    // Sprint 6 — flow builder visuale + test mode + versioning
    FLOW_VISUAL_LAYOUT_UPDATED: 'flow_visual_layout_updated',
    FLOW_ROLLBACK_TO_VERSION: 'flow_rollback_to_version',
    FLOW_VERSIONS_DIFF_VIEWED: 'flow_versions_diff_viewed',
    TEST_SESSION_STARTED: 'test_session_started',
    TEST_SESSION_TURN: 'test_session_turn',
    TEST_SESSION_DELETED: 'test_session_deleted',
    // Sprint 7 — API esterna v1
    API_KEY_CREATED: 'api_key_created',
    API_KEY_REVOKED: 'api_key_revoked',
    API_KEY_USED: 'api_key_used',
    IDENTITY_DECRYPT_VIA_API: 'identity_decrypt_via_api',
    PDF_DOWNLOADED_VIA_API: 'pdf_downloaded_via_api',
    // Sprint 7 — webhook
    WEBHOOK_CREATED: 'webhook_created',
    WEBHOOK_UPDATED: 'webhook_updated',
    WEBHOOK_DELETED: 'webhook_deleted',
    WEBHOOK_DELIVERY_OK: 'webhook_delivery_ok',
    WEBHOOK_DELIVERY_FAIL: 'webhook_delivery_fail',
    WEBHOOK_DELIVERY_EXHAUSTED: 'webhook_delivery_exhausted',
    WEBHOOK_TEST_SENT: 'webhook_test_sent',
    // Sprint 7 — GDPR cittadino
    GDPR_ACCESS_REQUEST_FULFILLED: 'gdpr_access_request_fulfilled',
    GDPR_DELETE_REQUEST_FULFILLED: 'gdpr_delete_request_fulfilled',
    BULK_EXPORT_PERFORMED: 'bulk_export_performed',
    RETENTION_EXECUTED: 'retention_executed',
    // Sprint 7 — kiosk
    SETTINGS_KIOSK_UPDATED: 'settings_kiosk_updated',
    KIOSK_EXIT_ATTEMPTED: 'kiosk_exit_attempted',
    // Sprint 8 — modalità voce
    SETTINGS_VOICE_UPDATED: 'settings_voice_updated',
    SETTINGS_VOICE_STT_KEY_SET: 'settings_voice_stt_key_set',
    SETTINGS_VOICE_STT_KEY_REMOVED: 'settings_voice_stt_key_removed',
    SETTINGS_VOICE_TTS_KEY_SET: 'settings_voice_tts_key_set',
    SETTINGS_VOICE_TTS_KEY_REMOVED: 'settings_voice_tts_key_removed',
    VOICE_STT_PERFORMED: 'voice_stt_performed',
    VOICE_TTS_PERFORMED: 'voice_tts_performed',
    VOICE_TURN_FAILED: 'voice_turn_failed',
    AUDIO_CAP_EXCEEDED: 'audio_cap_exceeded',
    AUDIO_SOFT_LIMIT_ALERT_SENT: 'audio_soft_limit_alert_sent',
    // Sprint 9a — diagnostica
    HEALTH_DETAILED_VIEWED: 'health_detailed_viewed',
};
/** Sprint 7 — formato API key */
export const API_KEY_PREFIX_LENGTH = 8; // primi 8 char per lookup
export const API_KEY_RANDOM_LENGTH = 32; // bytes random base32 dopo prefix
export const API_KEY_FORMAT_PREFIX = 'audq_'; // tutte le keys iniziano con questo
/** Sprint 7 — webhook retry schedule (delay in seconds) */
export const WEBHOOK_RETRY_SCHEDULE_SEC = [0, 30, 120, 600, 3600, 21600, 86400];
export const WEBHOOK_MAX_ATTEMPTS = WEBHOOK_RETRY_SCHEDULE_SEC.length;
export const WEBHOOK_HMAC_TOLERANCE_SEC = 300; // 5 min anti-replay
export const WEBHOOK_RESPONSE_BODY_MAX = 500;
/** Sprint 5 — limiti upload file */
export const BRANDING_LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2 MB
export const BRANDING_LOGO_MIME_ALLOWED = [
    'image/png',
    'image/jpeg',
    'image/svg+xml',
    'image/webp',
];
/** Sprint 5 — 2FA TOTP */
export const TOTP_RECOVERY_CODES_COUNT = 8;
export const TOTP_GRACE_DAYS = 7;
export const TOTP_PARTIAL_TOKEN_TTL_MIN = 5;
export const TOTP_PARTIAL_TOKEN_TYPE = 'complete-2fa';
/** Sprint 5 — cookie nome per partial token 2FA */
export const COOKIE_PARTIAL_2FA = '__Host-audiq_2fa';
/** Sprint 8 — voice mode */
// Costi (USD) per cost tracking — fonti: Deepgram pricing 2026, ElevenLabs Multilingual v2.
export const VOICE_COST_DEEPGRAM_PER_MIN_USD = 0.0077;
export const VOICE_COST_ELEVENLABS_PER_1K_CHARS_USD = 0.3;
// MIME types audio accettati dal backend STT (push-to-talk upload).
export const VOICE_AUDIO_MIME_ALLOWED = [
    'audio/webm',
    'audio/webm;codecs=opus',
    'audio/ogg',
    'audio/ogg;codecs=opus',
    'audio/mp4',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mpeg',
    'audio/wav',
];
// Tetto buffer audio user singolo turno (anti-abuse / cost protection).
// 30s a 64kbps webm/opus ≈ 240 KB; 60s ≈ 480 KB. Tetto 5 MB lascia margine generoso.
export const VOICE_AUDIO_MAX_BYTES = 5 * 1024 * 1024;
// Durata massima singolo turno user (sec). Oltre, taglia o rifiuta — l'utente è incoraggiato
// a dire una cosa alla volta. Ottimo anche per cost protection lato STT.
export const VOICE_AUDIO_MAX_SECONDS = 60;
// Cost-tracking codes per `usage_monthly.by_voice_provider`.
export const VOICE_PROVIDER_KEY_DEEPGRAM = 'deepgram';
export const VOICE_PROVIDER_KEY_ELEVENLABS = 'elevenlabs';
//# sourceMappingURL=constants.js.map